import React, { FC, useMemo } from 'react';

import { Box, Switch, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import {
  convertToDecimals,
  pharmacistEvent,
  PharmacistLegendVariant,
  WorkshiftStatus,
} from '@pharmaplan/common';

import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';
import { nameTypes } from '../../../Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';
import DateBox from '../../../Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/DateBox';
import { ReactComponent as CounterOfferIcon } from '../../../../../assets/svg/counterOfferMoney.svg';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { getDayMonthDateYear } from '../../../../Reports/helpers';
import ThemeConstants from '../../../../../theme/ThemeConstants';
import useSelfService from '../../../../../hooks/useSelfService';
import CustomCheckbox from '../../../../common/CustomCheckbox';
import { ChipType, MomentTimeFormats } from '../../../../../helpers/Constants';
import { serverToday } from '../../../../../helpers/Functions';
import CustomChip from '../../../../common/CustomChip';
import strings from '../../../../../localization';

import styles from './styles';

const { start, end, dayOff } = nameTypes;

interface IRequestContractTimes {
  isUpdate?: boolean;
  formik: FormikProps<any>;
  counterShifts: Array<string>;
}

const RequestContractTimes: FC<IRequestContractTimes> = ({
  formik,
  isUpdate,
  counterShifts,
}) => {
  const { isSelfService } = useSelfService();

  const event = useAppSelector(pharmacistEvent);
  const { shifts, longTermShiftContract, counterShiftContract } = event ?? {};
  const { weekDayPaidHourlyRate, weekendPaidHourlyRate } = longTermShiftContract ?? {};
  const {
    weekDayPaidHourlyRate: counterWeekdayRate,
    weekendPaidHourlyRate: counterWeekendRate,
  } = counterShiftContract ?? {};

  const { workshiftIds, isCounterOffer } = formik.values ?? {};
  const postedIds = useMemo(
    () =>
      shifts?.filter?.((item) =>
        item.status === WorkshiftStatus.Posted),
    [shifts],
  );

  const { weekdayHours, weekendHours } = useMemo(
    () =>
      getDaysDistribution(postedIds ?? []),
    [postedIds],
  );

  const sortedShifts = useMemo(
    () =>
      shifts
        ?.filter(
          (item) =>
            item.startDate
              >= (serverToday().toFormat(MomentTimeFormats.fullDate) ?? ''),
        )
        .toSorted?.((a, b) => {
          if (a?.startDate < b?.startDate) {
            return -1;
          }
          if (a?.startDate > b?.startDate) {
            return 1;
          }

          return 0;
        }) ?? [],
    [shifts],
  );

  const selectableShiftIds = useMemo(
    () =>
      sortedShifts
        .filter((item) =>
          item.status === WorkshiftStatus.Posted)
        .map((item) =>
          item.workShiftId)
        .filter((id): id is string =>
          Boolean(id)),
    [sortedShifts],
  );

  const isAllSelected = selectableShiftIds.length > 0
    && selectableShiftIds.every((id) =>
      workshiftIds?.includes(id));

  const handleCheckboxClick = (id: string) =>
    () => {
      if (workshiftIds?.includes(id)) {
        formik.setFieldValue(
          'workshiftIds',
          workshiftIds.filter((item: string) =>
            item !== id),
        );
      } else {
        formik.setFieldValue('workshiftIds', [...workshiftIds, id]);
      }
    };

  const handleSelectAll = () => {
    const selectedShiftIds = isAllSelected ? [] : selectableShiftIds;

    formik.setFieldValue('workshiftIds', selectedShiftIds);
  };

  const handleSwitch = () => {
    formik.setFieldValue('isCounterOffer', !isCounterOffer);
  };

  const weekdayRate = counterWeekdayRate || weekDayPaidHourlyRate;
  const weekendRate = counterWeekendRate || weekendPaidHourlyRate;

  const totalEarning = convertToDecimals(
    weekdayHours * (weekdayRate ?? 0) + weekendHours * (weekendRate ?? 0),
  ) as string;

  return (
    <Box>
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerLeftContainer}>
          <CounterOfferIcon />
          <Typography sx={styles.title}>
            {strings.formatString(
              strings.thisShiftIncludesXNumberOfShifts,
              `${postedIds?.length}`,
              totalEarning,
            )}
          </Typography>
        </Box>
        {isSelfService && (
          <Box sx={styles.headerRightContainer}>
            <Typography sx={styles.counterOfferText}>
              {strings.counterOffer}
              ?
            </Typography>
            <Switch
              onClick={handleSwitch}
              disabled={isUpdate}
              checked={isCounterOffer}
            />
          </Box>
        )}
      </Box>
      <Box sx={styles.titleContainer}>
        <Typography sx={styles.selectRequestText}>
          {`${strings.pleaseSelectDatesForRequest}. ${
            isCounterOffer ? strings.replaceCurrentOffer : ''
          }`}
        </Typography>
        <CustomCheckbox
          label={strings.selectAll}
          name="selectAll"
          handleChange={handleSelectAll}
          value={isAllSelected}
          id="selectAll"
        />
      </Box>
      <Box sx={styles.dateBoxGrid}>
        {sortedShifts?.map?.((interval) => {
          let Chip;
          const { startDate, workShiftId, status } = interval ?? {};
          const date = startDate?.split?.('T')?.[0];

          const isCounter = counterShifts.includes(date);

          const isBookedOrNotAvailable = status === WorkshiftStatus.Booked
            || status === WorkshiftStatus.NotAvailable;
          const isRequested = status === WorkshiftStatus.Requested;
          const disabled = !isCounterOffer || isBookedOrNotAvailable || isRequested;

          if (isBookedOrNotAvailable) {
            Chip = (
              <CustomChip
                type={ChipType.notAvailable}
                label={strings.notAvailable}
              />
            );
          }

          if (isRequested) {
            Chip = (
              <CustomChip
                type={PharmacistLegendVariant.requested}
                label={strings.requested}
              />
            );
          }

          return (
            <DateBox
              key={date}
              textColor={isCounter ? ThemeConstants.common.counterRed : null}
              showCheckbox={status === WorkshiftStatus.Posted}
              disabled={disabled}
              handleCheckboxClick={handleCheckboxClick(workShiftId ?? '')}
              checkboxValue={!!workshiftIds.includes(workShiftId)}
              changedName={`${date}|${nameTypes.changed}`}
              title={`${getDayMonthDateYear(date)}`}
              dayOffName={`${date}|${dayOff}`}
              startName={`${date}|${start}`}
              endName={`${date}|${end}`}
              formik={formik}
              Chip={Chip}
              hideDayOff
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default RequestContractTimes;
